/* eslint-disable react/no-array-index-key */
import React, { useState, useMemo, useRef, useImperativeHandle, useEffect } from 'react';
import clsx from 'clsx';

const VirtualizedGrid = React.forwardRef((props, ref) => {
    const {
        width, // outer width
        height, // outer height
        columns, // columns object: Array
        rows, // rows object: Array
        columnWidth, // column width function: (col, colIndex) => Number
        rowHeight, // row width function: (row, rowIndex) => Number
        className, // outer className
        style, // outer style
        onScroll, // onscroll event
        rowRender, // row render: ({ row, rowIndex, columns }) => JSX
        children, // cell render: (row, rowIndex, col, colIndex) => JSX
        virtualized = { column: true, row: true }, // virtualization direction
        prerender = { column: 0, row: 0 }, // pre-rendered length
        prerenderColumnIndexes, // pre-rendered column indexes: Array
        prerenderRowIndexes // pre-rendered row indexes: Array
    } = props;

    const columnWidthList = useMemo(() => (
        columns?.map((col, colIndex) => columnWidth?.(col, colIndex) || 0) || []
    ), [columns, columnWidth]);
    const rowHeightList = useMemo(() => (
        rows?.map((row, rowIndex) => rowHeight?.(row, rowIndex) || 0) || []
    ), [rows, rowHeight]);
    const columnLeft = useMemo(() => (
        columnWidthList.reduce((prev, _, index, list) => {
            const result = index ? prev[index - 1] + list[index - 1] : 0;
            prev.push(result);
            return prev;
        }, [])
    ), [columnWidthList]);
    const rowTop = useMemo(() => (
        rowHeightList.reduce((prev, _, index, list) => {
            const result = index ? prev[index - 1] + list[index - 1] : 0;
            prev.push(result);
            return prev;
        }, [])
    ), [rowHeightList]);
    const columnsWidth = columnWidthList.reduce((prev, curr) => prev + curr, 0);
    const rowsHeight = rowHeightList.reduce((prev, curr) => prev + curr, 0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [scrollTop, setScrollTop] = useState(0);
    const colStart = useMemo(() => {
        if (!virtualized?.column) {
            return 0;
        }
        let result;
        let position = 0;
        for (let i = 0; i < columnWidthList.length; i += 1) {
            position += columnWidthList[i];
            if (position > scrollLeft) {
                result = i;
                break;
            }
        }
        if (result === undefined) {
            result = 0;
        }
        if (prerender?.column) {
            result -= prerender.column || 0;
        }
        return Math.max(result, 0);
    }, [columnWidthList, scrollLeft, virtualized?.column, prerender?.column]);
    const colEnd = useMemo(() => {
        if (!virtualized?.column) {
            return columnWidthList.length - 1;
        }
        let result;
        let position = 0;
        for (let i = 0; i < columnWidthList.length; i += 1) {
            position += columnWidthList[i];
            if (position > scrollLeft + width) {
                result = i;
                break;
            }
        }
        if (result === undefined) {
            result = Math.max(columnWidthList.length - 1, 0);
        }
        if (prerender?.column) {
            result += prerender.column || 0;
        }
        return Math.min(result, columnWidthList.length - 1);
    }, [width, columnWidthList, scrollLeft, virtualized?.column, prerender?.column]);
    const rowStart = useMemo(() => {
        if (!virtualized?.row) {
            return 0;
        }
        let result;
        let position = 0;
        for (let i = 0; i < rowHeightList.length; i += 1) {
            position += rowHeightList[i];
            if (position > scrollTop) {
                result = i;
                break;
            }
        }
        if (result === undefined) {
            result = 0;
        }
        if (prerender?.row) {
            result -= prerender.row || 0;
        }
        return Math.max(result, 0);
    }, [rowHeightList, scrollTop, virtualized?.row, prerender?.row]);
    const rowEnd = useMemo(() => {
        if (!virtualized?.row) {
            return rowHeightList.length - 1;
        }
        let result;
        let position = 0;
        for (let i = 0; i < rowHeightList.length; i += 1) {
            position += rowHeightList[i];
            if (position > scrollTop + height) {
                result = i;
                break;
            }
        }
        if (result === undefined) {
            result = Math.max(rowHeightList.length - 1, 0);
        }
        if (prerender?.row) {
            result += prerender.row || 0;
        }
        return Math.min(result, rowHeightList.length - 1);
    }, [height, rowHeightList, scrollTop, virtualized?.row, prerender?.row]);
    const showColumns = useMemo(() => (
        columns.filter((_, colIndex) => (
            colIndex >= colStart && colIndex <= colEnd ||
            prerenderColumnIndexes?.includes(colIndex)
        ))
    ), [columns, colStart, colEnd, prerenderColumnIndexes]);

    const gridRef = useRef();
    const ScrollbarWidth = useMemo(() => {
        const scrollDiv = document.createElement('div');
        scrollDiv.className = 'table scrollbar-measure';
        document.body.appendChild(scrollDiv);
        const result = scrollDiv.offsetWidth - scrollDiv.clientWidth;
        document.body.removeChild(scrollDiv);
        return result;
    }, []);
    const scrollX = columnsWidth > width;
    const scrollY = rowsHeight > height && !className?.includes('hidden-scrollbar-y');
    useImperativeHandle(ref, () => ({
        getPosition: ({ columnIndex, rowIndex }) => ({
            left: columnLeft[columnIndex],
            top: rowTop[rowIndex],
            width: columnWidth[columnIndex],
            height: rowHeightList[rowIndex]
        }),
        scrollTo: (options) => gridRef.current?.scrollTo(options),
        scrollToItem: ({ align = 'auto', columnIndex, rowIndex }) => {
            const startOptions = { left: columnLeft[columnIndex], top: rowTop[rowIndex] };
            const endOptions = {
                left: columnLeft[columnIndex] + columnWidthList[columnIndex] -
                    (width - (scrollY ? ScrollbarWidth : 0)),
                top: rowTop[rowIndex] + rowHeightList[rowIndex] -
                    (height - (scrollX ? ScrollbarWidth : 0))
            };
            const centerOptions = {
                left: columnLeft[columnIndex] + columnWidthList[columnIndex] * 1 / 2 -
                    (width - (scrollY ? ScrollbarWidth : 0)) * 1 / 2,
                top: rowTop[rowIndex] + rowHeightList[rowIndex] * 1 / 2 -
                    (height - (scrollX ? ScrollbarWidth : 0)) * 1 / 2
            };
            let options = {};
            if (align === 'start') {
                options = startOptions;
            } else if (align === 'end') {
                options = endOptions;
            } else if (align === 'center') {
                options = centerOptions;
            } else if (align === 'smart') {
                if (scrollLeft < endOptions.left || scrollLeft > startOptions.left) {
                    options.left = centerOptions.left;
                }
                if (scrollTop < endOptions.top || scrollTop > startOptions.top) {
                    options.top = centerOptions.top;
                }
            } else {
                if (scrollLeft < endOptions.left) {
                    options.left = endOptions.left;
                } else if (scrollLeft > startOptions.left) {
                    options.left = startOptions.left;
                }
                if (scrollTop < endOptions.top) {
                    options.top = endOptions.top;
                } else if (scrollTop > startOptions.top) {
                    options.top = startOptions.top;
                }
            }
            if (columnIndex < 0 || columnIndex > columnLeft.length - 1) {
                delete options.left;
            }
            if (rowIndex < 0 || rowIndex > rowTop.length - 1) {
                delete options.top;
            }
            gridRef.current?.scrollTo(options);
        }
    }));

    useEffect(() => {
        if (!rows?.length) {
            setScrollLeft(0);
            setScrollTop(0);
            gridRef.current?.scrollTo({ top: 0, left: 0 });
            const e = { target: { scrollTop: 0, scrollLeft: 0 } };
            onScroll?.(e);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    return !!columns?.length && !!rows.length && (
        <div
            ref={gridRef}
            className={clsx('overflow-auto', className)}
            style={{ width, height, ...style }}
            onScroll={(e) => {
                const { target: { scrollLeft: left, scrollTop: top } } = e;
                setScrollLeft(left);
                setScrollTop(top);
                onScroll?.(e);
            }}
        >
            <div
                className="relative"
                style={{ width: columnsWidth, height: rowsHeight }}
            >
                {rows.map((row, rowIndex) => (
                    rowIndex >= rowStart && rowIndex <= rowEnd ||
                    prerenderRowIndexes?.includes(rowIndex)
                ) && (
                    <div
                        key={rowIndex}
                        className="flex absolute"
                        style={{
                            top: rowTop[rowIndex],
                            left: columnLeft[colStart],
                            height: rowHeightList[rowIndex] || 0
                        }}
                    >
                        {rowRender ? (
                            rowRender?.({ row, rowIndex, columns: showColumns })
                        ) : (
                            columns.map((col, colIndex) => (
                                colIndex >= colStart && colIndex <= colEnd ||
                                prerenderColumnIndexes?.includes(colIndex)
                            ) && (
                                <div
                                    key={colIndex}
                                    className="h-full"
                                    style={{ width: columnWidthList[colIndex] || 0 }}
                                >
                                    {typeof children === 'function' ?
                                        children?.({ row, rowIndex, col, colIndex }) :
                                        children}
                                </div>
                            ))
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
});

export default VirtualizedGrid;
